/**
 * 알림 & 지원 공통 유틸 (상세/편집 공용)
 * - 목업 데이터 로딩, 섹션 설정, 상세/편집 경로 생성
 */
import {
  readBoardListLastPath,
  writeBoardListLastPath,
  isBoardListStoredPathValid,
} from '../../assets/utils/comm/boardListPathPersistence';
import { PATH_BBS_TP_MAP } from '../bbs/types/bbsTpTypes';

export const SECTION_NEWS = 'glow-news';
export const SECTION_PERFORMANCE = 'glow-performance';
export const SECTION_SUPPORT_CENTER = 'support-center';

/** sessionStorage: 목록 URL(쿼리 포함) — 상세에서 state 손실 시에도 페이지 복귀 */
export function supportListLastPathStorageKey(section, listKey) {
  return `glow:support:lastListPath:${section}:${listKey || 'default'}`;
}

export function readSupportListLastPath(section, listKey) {
  if (!section || !listKey) return '';
  return readBoardListLastPath(supportListLastPathStorageKey(section, listKey));
}

export function writeSupportListLastPath(section, listKey, pathnameAndSearch) {
  if (!section || !listKey || typeof pathnameAndSearch !== 'string') return;
  const base = getListHref(section, listKey);
  if (!base || base === '#') return;
  writeBoardListLastPath(supportListLastPathStorageKey(section, listKey), base, pathnameAndSearch);
}

/** 저장된 경로가 해당 게시판 목록(base)과 일치하는지 (오조작 방지) */
export function isSupportStoredListPathValid(base, stored) {
  return isBoardListStoredPathValid(base, stored);
}

/** 섹션별 브레드크럼 표시명 (알림 & 지원 다음 단계 명확화) */
export const SECTION_BREADCRUMB_LABELS = {
  [SECTION_NEWS]: 'GLOW 소식',
  [SECTION_PERFORMANCE]: 'GLOW 성과 & 현황',
  [SECTION_SUPPORT_CENTER]: '지원'
};

/** GLOW 소식 탭 라벨 (브레드크럼 등) */
export const GLOW_NEWS_TAB_LABELS = { notice: '공지사항', news: 'GLOW NEWS' };

/** GLOW 소식 탭 순서 (URL tab 파라미터 0~1) */
export const GLOW_NEWS_TAB_TYPES = ['notice', 'news'];

/** GLOW 소식 listKey → URL용 탭 인덱스 (0~1) */
export function getGlowNewsTabIndex(listKey) {
  const idx = GLOW_NEWS_TAB_TYPES.indexOf(listKey);
  return idx >= 0 ? idx : 0;
}

/** GLOW 성과 & 현황 탭 라벨 (브레드크럼 등) */
export const GLOW_PERFORMANCE_TAB_LABELS = {
  matching: '매칭·협업 현황',
  case: 'Alliance 사례 소개',
  portfolio: 'Alliance 성과·포트폴리오'
};

/** GLOW 성과 & 현황 탭 순서 (URL tab 파라미터 0~2) */
export const GLOW_PERFORMANCE_TAB_TYPES = ['matching', 'case', 'portfolio'];

/** GLOW 성과 & 현황 listKey → URL용 탭 인덱스 (0~2) */
export function getGlowPerformanceTabIndex(listKey) {
  const idx = GLOW_PERFORMANCE_TAB_TYPES.indexOf(listKey);
  return idx >= 0 ? idx : 0;
}

export const SUPPORT_CENTER_TAB_LABELS = {
  library: '자료실',
  manual: '이용 매뉴얼',
  faq: 'FAQ',
  inquiry: '문의하기'
};

/** 지원 센터 탭 순서 (URL tab 파라미터 숫자와 매칭: 0~3) */
export const SUPPORT_CENTER_TAB_TYPES = ['library', 'manual', 'faq', 'inquiry'];

/** listKey → URL용 탭 인덱스 (0~3) */
export function getSupportCenterTabIndex(listKey) {
  const idx = SUPPORT_CENTER_TAB_TYPES.indexOf(listKey);
  return idx >= 0 ? idx : 0;
}

/**
 * 편집 화면 첨부파일 영역 표시 여부 (게시물별).
 * - 보임: GLOW 소식(공지/GLOW NEWS), GLOW 성과&현황(매칭·협업/Alliance 사례/성과·포트폴리오), 지원(자료실/이용 매뉴얼/문의하기)
 * - 숨김: 지원 > FAQ
 */
export function getShowAttachments(section, listKey) {
  if (section === SECTION_SUPPORT_CENTER && listKey === 'faq') return false;
  return true;
}

/** 섹션별 목록/상세/편집 링크 */
export const SECTION_CONFIG = {
  [SECTION_NEWS]: {
    listHref: '/support/glow-news/notice',
    breadcrumbHref: '/support/glow-news',
    metaLeft: [
      { label: '작성자', key: 'writer' },
      { label: '등록일', key: 'date' }
    ],
    metaRight: [{ label: '조회수', key: 'views' }],
    typeLabelKey: 'type',
    contentMode: 'html',
    emptyMessage: '해당 게시글을 찾을 수 없습니다.',
    showAttachments: getShowAttachments
  },
  [SECTION_PERFORMANCE]: {
    listHref: '/support/glow-performance',
    breadcrumbHref: '/support/glow-performance',
    metaLeft: [
      { label: '작성자', key: 'writer' },
      { label: '등록일', key: 'date' }
    ],
    metaRight: [{ label: '조회수', key: 'views' }],
    typeLabelKey: null,
    contentMode: 'html',
    emptyMessage: '해당 항목을 찾을 수 없습니다.',
    showAttachments: getShowAttachments
  },
  [SECTION_SUPPORT_CENTER]: {
    listHref: '/support/support-center',
    breadcrumbHref: '/support/support-center',
    metaLeft: [
      { label: '작성자', key: 'writer' },
      { label: '등록일', key: 'date' }
    ],
    metaRight: [{ label: '조회수', key: 'views' }],
    typeLabelKey: null,
    contentMode: 'html',
    emptyMessage: '해당 항목을 찾을 수 없습니다.',
    showAttachments: getShowAttachments
  }
};

/** 목록 페이지 경로 (GLOW 소식·성과·지원센터는 탭별 페이지로 분리) */
export function getListHref(section, listKey) {
  if (section === SECTION_NEWS && listKey) return `/support/glow-news/${listKey}`;
  if (section === SECTION_PERFORMANCE && listKey) return `/support/glow-performance/${listKey}`;
  if (section === SECTION_SUPPORT_CENTER && listKey) return `/support/support-center/${listKey}`;
  const config = SECTION_CONFIG[section];
  return config?.listHref ?? '#';
}

/** 상세 페이지 경로 */
export function getDetailPath(section, listKey, no) {
  if (section === SECTION_NEWS) return `/support/glow-news/detail/${listKey}/${no}`;
  if (section === SECTION_PERFORMANCE) return `/support/glow-performance/detail/${listKey}/${no}`;
  if (section === SECTION_SUPPORT_CENTER) return `/support/support-center/detail/${listKey}/${no}`;
  return '#';
}

/** 편집 페이지 경로 */
export function getEditPath(section, listKey, no) {
  if (section === SECTION_NEWS) return `/support/glow-news/edit/${listKey}/${no}`;
  if (section === SECTION_PERFORMANCE) return `/support/glow-performance/edit/${listKey}/${no}`;
  if (section === SECTION_SUPPORT_CENTER) return `/support/support-center/edit/${listKey}/${no}`;
  return '#';
}

/** 신규 작성 페이지 경로 (no = 'new') */
export function getNewEditPath(section, listKey) {
  return getEditPath(section, listKey, 'new');
}

/** pathname으로 섹션 판별 */
export function getSectionFromPathname(pathname) {
  if (pathname.includes('support-center')) return SECTION_SUPPORT_CENTER;
  if (pathname.includes('glow-performance')) return SECTION_PERFORMANCE;
  if (pathname.includes('glow-news')) return SECTION_NEWS;
  return null;
}

/** [개선] pathname에서 bbstpcd 추출 (중앙 매핑 상수 활용) */
export function getBbsTpCd(pathname) {
  if (!pathname) return null;

  // PATH_BBS_TP_MAP의 키(경로 키워드)가 pathname에 포함되어 있는지 확인
  const matchedKey = Object.keys(PATH_BBS_TP_MAP).find(key => pathname.includes(key));
  return matchedKey ? PATH_BBS_TP_MAP[matchedKey] : null;
}
